import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import { setMessageError } from "app/pages/sales-intelligence/modules/ai-agent/store";
import { useFindDuplicateSignalDraft } from "app/pages/sales-intelligence/sub-modules/signals/hooks/useFindDuplicateSignalDraft";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import type SignalCreateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalCreateDTO";
import { useCreateSignalMutation } from "app/pages/sales-intelligence/sub-modules/signals/query/mutations/useCreateSignalMutation";
import {
    addAppliedSignalsAction,
    draftSignalCreatedAndApplied,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";

/**
 * @remarks
 * This hook provides idempotent signal creation:
 * - If a duplicate signal exists, returns the existing signal (no API call)
 * - If no duplicate, creates a new signal via API
 * - Both cases are treated as success, ensuring UI displays the signal
 */
export const useCreateSignalDraftFromMessage = (agentMessageId: string): ReturnType<typeof useCreateSignalMutation> => {
    const dispatch = useDispatch();

    const findDuplicate = useFindDuplicateSignalDraft();

    return useCreateSignalMutation({
        keyIdentifier: agentMessageId,

        findDuplicate: (signalDto: SignalCreateDTO<SignalFilters>) => {
            const existingSignal = findDuplicate(signalDto);

            if (existingSignal) {
                Sentry.errorTracker({
                    message: "Found existing signal instead of creating duplicate",
                    e: new Error("Duplicate signal detected"),
                    level: "warning",
                    params: {
                        extra: {
                            messageId: agentMessageId,
                            existingSignalId: existingSignal.id,
                            signal: signalDto,
                        },
                    },
                });
            }

            return existingSignal;
        },

        onSuccess({ signal }) {
            dispatch(addAppliedSignalsAction([signal.id]));
            dispatch(draftSignalCreatedAndApplied({ signalId: signal.id }));
        },

        onError(error, signalDto) {
            dispatch(
                setMessageError({
                    messageId: agentMessageId,
                    error: translate("si.signals.error.create-signal.text"),
                }),
            );

            Sentry.errorTracker({
                message: "Failed to create signal from AI message",
                e: error,
                level: "error",
                params: {
                    extra: {
                        messageId: agentMessageId,
                        signal: signalDto,
                    },
                },
            });
        },
    });
};
